// @ts-strict-ignore
import { ConfigFileSection } from '@/types';
import { ConfigFileManagedButton } from '@/components/HOCs/ConfigFileManagedMessage';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import { RootState } from '../../../reducers/reducers';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Popconfirm from 'antd/lib/popconfirm';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { TagsOutlined, PlusOutlined, MinusCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import useForm from 'antd/lib/form/hooks/useForm';
import {
    selectAppSettings,
    selectFeatureManagementIntegration,
    selectWasAppCreatedAfterQualitativeOnboarding,
    selectUserIsAdmin,
    selectIsRunwayOrg,
} from '../../../selectors/selectors';
import { AppComponentProps, SetupStep, SetupStepType, SetupViewProps } from '../../../typescriptHelpers';
import {
    DisallowedTokensForFA,
    FeatureAffiliation,
    IntegrationCapability,
    IntegrationType,
    RequireAtLeastOneOfTokensForFA,
} from '../../../types';
import { displayNameForIntegrationType, iconAndAltForIntegrationType } from '../../../helpers/integrationHelpers';
import { AutomationTip, HelpTip, InfoTip, setupModuleStyle } from '../shared';
import { antGrey, antRed, runwayColors } from '../../styles/globalStyles';
import {
    allSupportedIntegrationTypesForCapability,
    displayNameForFeatureAffiliation,
    featureAffiliationTypeForIntegrationType,
    validatePattern,
} from '../../../helpers';
import {
    DeleteFeatureAffiliationRequest,
    NewFeatureAffiliationRequest,
    tryUpdateAppSettings,
    UpdateFeatureAffiliationPatternRequest,
} from '../../../actions/appSettings';
import { AppSettingsUpdatable } from '../../../actions/updatable';
import { getSetupStepLocalData } from '../../../helpers/localStorageHelper';
import { globalMessage } from '../../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const FEATURE_AFFILIATION_TYPES = {
    [IntegrationType.Jira]: featureAffiliationTypeForIntegrationType(IntegrationType.Jira),
    [IntegrationType.Linear]: featureAffiliationTypeForIntegrationType(IntegrationType.Linear),
    [IntegrationType.Pivotal]: featureAffiliationTypeForIntegrationType(IntegrationType.Pivotal),
    [IntegrationType.Asana]: featureAffiliationTypeForIntegrationType(IntegrationType.Asana),
    [IntegrationType.Shortcut]: featureAffiliationTypeForIntegrationType(IntegrationType.Shortcut),
    [IntegrationType.AzureIT]: featureAffiliationTypeForIntegrationType(IntegrationType.AzureIT),
    [IntegrationType.Monday]: featureAffiliationTypeForIntegrationType(IntegrationType.Monday),
    [IntegrationType.GitHubIT]: featureAffiliationTypeForIntegrationType(IntegrationType.GitHubIT),
};

type featureManagementKeys = keyof typeof FEATURE_AFFILIATION_TYPES;

interface FeatureAffiliationFormProps extends SetupViewProps {
    featureAffiliations: FeatureAffiliation[];
}

const FeatureAffiliationForm: React.FC<FeatureAffiliationFormProps> = ({
    orgId,
    appId,
    onChange,
    featureAffiliations,
}) => {
    const dispatch = useDispatch();
    const [form] = useForm();
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const allFeatureManagementIntegrations = allSupportedIntegrationTypesForCapability(
        IntegrationCapability.FeatureManagement,
        isRunwayOrg,
        orgId,
    );
    const integration = useSelector((state: RootState) => selectFeatureManagementIntegration(state, appId));
    const [icon, alt] = integration ? iconAndAltForIntegrationType(integration.id) : [null, null];

    const [selectedFA, setSelectedFA] = React.useState(null);
    const [selectedFAIndex, setSelectedFAIndex] = React.useState(null);

    const handleFinish = (idx) => () => {
        setSelectedFAIndex(idx);
        form.validateFields().then((values) => {
            const fa = values['featureAffiliations'][idx];
            setSelectedFA(fa.id);
            if (!values['featureAffiliations'][idx].id) {
                addFeatureAffiliation(values, idx);
            } else {
                updateFeatureAffiliation(values, idx);
            }
        });
    };

    React.useEffect(() => {
        form.resetFields();
    }, [integration]);

    //ADD

    const newFeatureAffiliationIsLoading = useIsLoading(AppSettingsUpdatable.NewFeatureAffiliation);
    const newFeatureAffiliationSuccess = useUpdated(AppSettingsUpdatable.NewFeatureAffiliation);
    const newFeatureAffiliationError = useError(AppSettingsUpdatable.NewFeatureAffiliation);

    const addFeatureAffiliation = (values, idx) => {
        const request: NewFeatureAffiliationRequest = {
            appId,
            orgId,
            integrationId: values['integrationId'],
            pattern: values['featureAffiliations'][idx].pattern,
            affiliationType: values['featureAffiliations'][idx].affiliationType,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.NewFeatureAffiliation, request));
    };

    React.useEffect(() => {
        if (newFeatureAffiliationSuccess) {
            globalMessage.success('New feature affiliation added!');
            onChange(false);
            form.resetFields(); //so remove button will appear
        }
    }, [newFeatureAffiliationSuccess]);

    //UPDATE

    const editFeatureAffiliationIsLoading = useIsLoading(AppSettingsUpdatable.UpdateFeatureAffiliation, selectedFA);
    const editFeatureAffiliationError = useError(AppSettingsUpdatable.UpdateFeatureAffiliation, selectedFA);
    const editFeatureAffiliationSuccess = useUpdated(AppSettingsUpdatable.UpdateFeatureAffiliation, selectedFA);

    const updateFeatureAffiliation = (values, idx) => {
        const id = values['featureAffiliations'][idx].id;
        const request: UpdateFeatureAffiliationPatternRequest = {
            appId,
            orgId,
            featureAffiliationId: id,
            pattern: values['featureAffiliations'][idx].pattern,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.UpdateFeatureAffiliation, request, id));
    };

    React.useEffect(() => {
        if (editFeatureAffiliationSuccess) {
            globalMessage.success('Feature affiliation updated!');
            onChange(false);
        }
    }, [editFeatureAffiliationSuccess]);

    //DELETE

    const deleteFeatureAffiliationIsLoading = useIsLoading(AppSettingsUpdatable.DeleteFeatureAffiliation);
    const deleteFeatureAffiliationError = useError(AppSettingsUpdatable.DeleteFeatureAffiliation);
    const deleteFeatureAffiliationSuccess = useUpdated(AppSettingsUpdatable.DeleteFeatureAffiliation);

    const deleteFeatureAffiliation = (id, idx) => {
        setSelectedFAIndex(idx);
        const request: DeleteFeatureAffiliationRequest = {
            orgId,
            appId,
            featureAffiliationId: id,
        };
        dispatch(tryUpdateAppSettings(AppSettingsUpdatable.DeleteFeatureAffiliation, request));
    };

    React.useEffect(() => {
        if (deleteFeatureAffiliationSuccess) {
            globalMessage.success('Feature affiliation removed.');
            onChange(false);
            form.resetFields();
        }
    }, [deleteFeatureAffiliationSuccess]);

    React.useEffect(() => {
        if (newFeatureAffiliationError) {
            globalMessage.error(newFeatureAffiliationError);
        } else if (editFeatureAffiliationError) {
            globalMessage.error(editFeatureAffiliationError);
        } else if (deleteFeatureAffiliationError) {
            globalMessage.error(deleteFeatureAffiliationError);
        }
    }, [newFeatureAffiliationError, editFeatureAffiliationError, deleteFeatureAffiliationError]);

    const handleIntegrationChange = (values) => {
        form.setFieldsValue(
            featureAffiliations.length === 0 || values !== initialValues.integrationId ?
                {
                    featureAffiliations: [
                        { affiliationType: FEATURE_AFFILIATION_TYPES[values as featureManagementKeys][0] },
                    ],
                }
            :   {
                    integrationId: featureAffiliations[0].integrationId,
                    featureAffiliations: featureAffiliations,
                },
        );
    };

    const checkIfSectionHasUnsavedChanged = (idx) => {
        const faSectionValues = form.getFieldValue('featureAffiliations')[idx];
        return !isEqual(faSectionValues, initialValues.featureAffiliations[idx]);
    };

    const checkIfHasUnsavedChanges = () => {
        const formValues = form.getFieldsValue();
        return !isEqual(formValues, initialValues);
    };

    const initialValues =
        featureAffiliations.length === 0 ?
            {
                integrationId: integration ? integration.id : allFeatureManagementIntegrations[0],
                featureAffiliations: [
                    {
                        affiliationType:
                            FEATURE_AFFILIATION_TYPES[
                                integration ?
                                    integration.id
                                :   (allFeatureManagementIntegrations[0] as featureManagementKeys)
                            ][0],
                        pattern: '',
                    },
                ],
            }
        :   {
                integrationId: featureAffiliations[0].integrationId,
                featureAffiliations: featureAffiliations,
            };

    return (
        <Form
            form={form}
            name="newFeatureAffiliation"
            layout="horizontal"
            labelAlign="left"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            preserve={false}
            colon={false}
            autoComplete="off"
            initialValues={initialValues}
            onFieldsChange={() => onChange(checkIfHasUnsavedChanges())}
            validateTrigger="onFinish"
            style={{ display: 'flex' }}
        >
            <div>
                {integration && <img src={icon} alt={alt} width={30} height={30} />}
                <div style={{ display: `${integration ? 'none' : 'initial'}` }}>
                    <Typography.Text style={{ color: antGrey.grey8, fontWeight: 400 }}>
                        Project management tool
                    </Typography.Text>
                    <Form.Item name="integrationId" style={{ marginTop: 20 }} wrapperCol={{ span: 24 }}>
                        <Select onChange={handleIntegrationChange}>
                            {allFeatureManagementIntegrations.map((integration, index) => {
                                const displayName = displayNameForIntegrationType(integration);
                                return (
                                    <Select.Option key={index} value={integration}>
                                        {displayName}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                </div>
            </div>
            <Divider type="vertical" style={{ height: 'auto', margin: '0px 24px', backgroundColor: antGrey.grey5 }} />
            <div style={{ flex: 2, maxWidth: 550 }}>
                <Typography.Text style={{ color: antGrey.grey8, fontWeight: 400 }}>
                    Define tags that you use to associate work with a release
                </Typography.Text>
                <Form.List name="featureAffiliations">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }, idx) => (
                                <div
                                    key={key}
                                    style={{
                                        borderRadius: 4,
                                        backgroundColor: runwayColors.white,
                                        padding: 20,
                                        margin: '20px 0px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                        }}
                                    >
                                        <div style={{ flex: 1 }}>
                                            <Form.Item
                                                noStyle
                                                shouldUpdate={(prevValues, curValues) =>
                                                    prevValues.integrationId !== curValues.integrationId
                                                }
                                            >
                                                {() => {
                                                    return (
                                                        <Form.Item
                                                            label="Affiliation type"
                                                            name={[name, 'affiliationType']}
                                                            {...restField}
                                                        >
                                                            <Select>
                                                                {(
                                                                    FEATURE_AFFILIATION_TYPES[
                                                                        form.getFieldValue(
                                                                            'integrationId',
                                                                        ) as featureManagementKeys
                                                                    ] || []
                                                                ).map((item) => (
                                                                    <Select.Option key={item} value={item}>
                                                                        {displayNameForFeatureAffiliation(item)}
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </Form.Item>
                                                    );
                                                }}
                                            </Form.Item>
                                            <Form.Item
                                                label="Affiliation pattern"
                                                name={[name, 'pattern']}
                                                {...restField}
                                                style={{ marginBottom: 0 }}
                                                rules={[
                                                    {
                                                        validator: (rule, value) => {
                                                            const pattern = value as string;

                                                            return validatePattern(
                                                                pattern,
                                                                'Feature affiliation',
                                                                RequireAtLeastOneOfTokensForFA,
                                                                DisallowedTokensForFA,
                                                            );
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Input placeholder="e.g. release-{version}" autoComplete="off" />
                                            </Form.Item>
                                        </div>
                                        {!form.getFieldValue('featureAffiliations')[idx].id && idx !== 0 && (
                                            <div>
                                                <MinusCircleOutlined
                                                    style={{ color: antRed.red5 }}
                                                    onClick={() => remove(name)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                        {form.getFieldValue('featureAffiliations')[idx].id && (
                                            <Form.Item style={{ marginBottom: 0 }}>
                                                <Popconfirm
                                                    placement="topRight"
                                                    title={'Are you sure you want to delete this feature affiliation?'}
                                                    okButtonProps={{
                                                        loading:
                                                            deleteFeatureAffiliationIsLoading &&
                                                            idx === selectedFAIndex,
                                                    }}
                                                    onConfirm={() =>
                                                        deleteFeatureAffiliation(
                                                            form.getFieldValue('featureAffiliations')[idx].id,
                                                            idx,
                                                        )
                                                    }
                                                >
                                                    <ConfigFileManagedButton
                                                        configFileSection={ConfigFileSection.FeatureAffiliations}
                                                        type="link"
                                                        danger
                                                        size="small"
                                                        loading={
                                                            deleteFeatureAffiliationIsLoading && idx === selectedFAIndex
                                                        }
                                                    >
                                                        Remove
                                                    </ConfigFileManagedButton>
                                                </Popconfirm>
                                            </Form.Item>
                                        )}
                                        <Form.Item style={{ marginBottom: 0 }} shouldUpdate>
                                            {() => (
                                                <ConfigFileManagedButton
                                                    configFileSection={ConfigFileSection.FeatureAffiliations}
                                                    type="primary"
                                                    size="small"
                                                    loading={
                                                        (newFeatureAffiliationIsLoading ||
                                                            editFeatureAffiliationIsLoading) &&
                                                        idx === selectedFAIndex
                                                    }
                                                    htmlType="submit"
                                                    icon={<ArrowRightOutlined />}
                                                    disabled={!checkIfSectionHasUnsavedChanged(idx)}
                                                    onClick={handleFinish(idx)}
                                                >
                                                    Save
                                                </ConfigFileManagedButton>
                                            )}
                                        </Form.Item>
                                    </div>
                                </div>
                            ))}
                            <Form.Item wrapperCol={{ span: 12 }}>
                                <ConfigFileManagedButton
                                    configFileSection={ConfigFileSection.FeatureAffiliations}
                                    onClick={() =>
                                        add({
                                            affiliationType:
                                                FEATURE_AFFILIATION_TYPES[
                                                    form.getFieldValue('integrationId') as featureManagementKeys
                                                ][0],
                                        })
                                    }
                                    icon={<PlusOutlined />}
                                >
                                    Add another
                                </ConfigFileManagedButton>
                            </Form.Item>
                        </>
                    )}
                </Form.List>
            </div>
        </Form>
    );
};

const SetupViewComponent: React.FC<SetupViewProps> = (props) => {
    const featureAffiliations = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).featureAffiliations,
    );

    return (
        <div
            style={{
                ...setupModuleStyle,
            }}
        >
            <FeatureAffiliationForm {...props} featureAffiliations={featureAffiliations} />
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                Runway can pull in associated project tasks and intelligently reconcile what’s happening in Jira with
                what’s happening in GitHub (for example), for a more accurate sense of how work is progressing.{' '}
            </InfoTip>
            <InfoTip>
                For teams that run release trains (and don’t regularly label work), labeled work items can still be
                useful for flagging any critical showstoppers that need to go out with a given version.{' '}
            </InfoTip>
            <AutomationTip>
                <span>
                    Runway can automatically label any tickets that are associated with code work that has been found in
                    the release. Read more about tokenized patterns in our{' '}
                    <Typography.Link
                        href="https://docs.runway.team/getting-started/pattern-strings-tokens"
                        target="_blank"
                    >
                        docs.
                    </Typography.Link>
                </span>
            </AutomationTip>
            <HelpTip>
                Questions, or want a walkthrough?{' '}
                <Button type="link" style={{ padding: 0, display: 'inline', marginLeft: 5 }} href={`#hs-chat-open`}>
                    Let us know!
                </Button>
            </HelpTip>
        </Space>
    );
};

export const FeatureAffiliationStep: SetupStep = {
    title: 'How do you label items in project management?',
    description: () => {
        return 'Runway will look for your specified labels (known as “feature affiliations” in Runway) when determining which work to associate with a given release. Runway can also automatically apply these labels where needed.';
    },
    icon: () => TagsOutlined,
    SetupViewComponent,
    InfoViewComponent,
    listTitle: 'Project labels',
    listDescription: 'How do you label items in project management?',
    integrationsRecommendationBannerData: {
        title: () => {
            return 'Recommended: Connect your project management tool';
        },
        description: 'Automate manual tasks, and reconcile items of work between ticket and code references.',
        actionText: 'OK, let’s do it',
        type: SetupStepType.FeatureAffiliations,
        integrationCapabilities: [IntegrationCapability.FeatureManagement],
    },
    isCompleteSelector: (root, { appId }) => {
        const isUserAdmin = selectUserIsAdmin(root);
        const wasVisited = getSetupStepLocalData(appId, SetupStepType.FeatureAffiliations)?.hasBeenVisited;

        const wasAppCreatedAfterQualitativeOnboarding = selectWasAppCreatedAfterQualitativeOnboarding(root, appId);

        return wasAppCreatedAfterQualitativeOnboarding && isUserAdmin ? wasVisited : true;
    },
};
